import {
	Alert, Autocomplete, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { getLoading } from "../../pages/execution-events/ExecutionEvents";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskCompanyDTO, RiskLinkTreeDTO, RiskSectorDTO } from "../../types/RiskAssessmentDTO";
import { EditorRow, LINK_SOURCE_COMPANY, duplicatePositions, findTemplate, templateChoices } from "./riskEditorState";
import './RiskAddRowModal.scss';

interface Props {
	open: boolean;
	company: RiskCompanyDTO;
	rows: EditorRow[];
	onClose: () => void;
	onConfirm: (sector: string, positions: string[], risk: string, tree: RiskLinkTreeDTO, template: EditorRow | null) => void;
}

const RiskAddRowModal: FC<Props> = ({ open, company, rows, onClose, onConfirm }) => {
	const [structure, setStructure] = useState<RiskSectorDTO[] | null>(null);
	const [tree, setTree] = useState<RiskLinkTreeDTO | null>(null);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [sector, setSector] = useState<string | null>(null);
	const [positions, setPositions] = useState<string[]>([]);
	const [risk, setRisk] = useState<string | null>(null);
	const [copy, setCopy] = useState(false);
	const [copySector, setCopySector] = useState<string | null>(null);
	const [copyPosition, setCopyPosition] = useState<string | null>(null);
	const [copyRisk, setCopyRisk] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setSector(null);
			setPositions([]);
			setRisk(null);
			setCopy(false);
			setCopySector(null);
			setCopyPosition(null);
			setCopyRisk(null);
			if ((!structure || !tree) && !loading) {
				load();
			}
		}
	}, [open]);

	const sectorOptions = useMemo(() => (structure || []).map(item => item.sector), [structure]);
	const positionOptions = useMemo(() => (structure || []).find(item => item.sector === sector)?.positions || [],
		[structure, sector]);
	const riskOptions = useMemo(() => (tree?.links || []).map(link => link.risk)
		.sort((first, second) => first.localeCompare(second, 'pt-BR', { sensitivity: 'base' })), [tree]);
	const duplicates = useMemo(() => sector && risk ? duplicatePositions(rows, sector, positions, risk) : [],
		[rows, sector, positions, risk]);
	const choices = useMemo(() => copy ? templateChoices(rows, copySector, copyPosition) : null,
		[copy, rows, copySector, copyPosition]);
	const template = copy && copySector && copyPosition && copyRisk
		? findTemplate(rows, copySector, copyPosition, copyRisk)
		: null;
	const canConfirm = !!sector && !!positions.length && !!risk && !duplicates.length && !!tree && (!copy || !!template);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth id="modal-risk-add-row" className="risk-add-row">
			<DialogTitle>Adicionar risco</DialogTitle>
			<DialogContent>
				{loading
					? getLoading()
					: failed
						? <div className="vazio">
							<span>Não foi possível carregar os setores, cargos e riscos.</span>
							<Button variant="outlined" onClick={load}>Tentar novamente</Button>
						</div>
						: <div className="campos">
							<p className="descricao">
								Cria uma linha de risco vazia para cada cargo escolhido. Os riscos disponíveis são os da
								empresa {LINK_SOURCE_COMPANY}; o restante é preenchido depois na tabela.
							</p>
							<Autocomplete
								options={sectorOptions}
								value={sector}
								onChange={(_, next) => {
									setSector(next);
									setPositions([]);
								}}
								renderInput={(params) => <TextField {...params} required label="Setor" size="small"/>}
								noOptionsText="Nenhum setor cadastrado na empresa"
							/>
							<Autocomplete
								multiple
								disableCloseOnSelect
								disabled={!sector}
								options={positionOptions}
								value={positions}
								onChange={(_, next) => setPositions(next)}
								renderOption={(props, option, { selected }) => (
									<li {...props} key={option}>
										<Checkbox size="small" checked={selected} sx={{ mr: 1 }}/>
										{option}
									</li>
								)}
								renderInput={(params) => (
									<TextField {...params} required label="Cargos" size="small"
										helperText={sector ? 'Cargos vinculados ao setor no SGG' : 'Escolha o setor primeiro'}/>
								)}
								noOptionsText="Nenhum cargo vinculado a este setor"
							/>
							<Autocomplete
								options={riskOptions}
								value={risk}
								onChange={(_, next) => setRisk(next)}
								renderInput={(params) => <TextField {...params} required label="Risco" size="small"/>}
								noOptionsText="Nenhum risco encontrado"
							/>
							<FormControlLabel
								control={<Switch checked={copy} onChange={(event) => setCopy(event.target.checked)}/>}
								label="Copiar campos de risco existente"
							/>
							{copy && (
								<div className="copia">
									<p className="descricao">
										Copia os valores e as sub-linhas de um risco da planilha para as linhas novas. Setor, cargos e
										risco continuam os escolhidos acima.
									</p>
									<Autocomplete
										options={choices?.sectors || []}
										value={copySector}
										onChange={(_, next) => {
											setCopySector(next);
											setCopyPosition(null);
											setCopyRisk(null);
										}}
										renderInput={(params) => <TextField {...params} required label="Setor da planilha" size="small"/>}
										noOptionsText="Nenhum setor encontrado"
									/>
									<Autocomplete
										disabled={!copySector}
										options={choices?.positions || []}
										value={copyPosition}
										onChange={(_, next) => {
											setCopyPosition(next);
											setCopyRisk(null);
										}}
										renderInput={(params) => <TextField {...params} required label="Cargo da planilha" size="small"/>}
										noOptionsText="Nenhum cargo encontrado"
									/>
									<Autocomplete
										disabled={!copyPosition}
										options={choices?.risks || []}
										value={copyRisk}
										onChange={(_, next) => setCopyRisk(next)}
										renderInput={(params) => <TextField {...params} required label="Risco da planilha" size="small"/>}
										noOptionsText="Nenhum risco encontrado"
									/>
								</div>
							)}
							{!!duplicates.length && (
								<Alert severity="warning">
									Este risco já existe para {duplicates.join(', ')} neste setor. Retire {duplicates.length === 1
										? 'esse cargo' : 'esses cargos'} da seleção para continuar.
								</Alert>
							)}
						</div>
				}
			</DialogContent>
			<DialogActions>
				<Button id="risk-add-row-close-button" onClick={onClose}>Cancelar</Button>
				<Button
					id="risk-add-row-confirm-button"
					variant="contained"
					disabled={!canConfirm}
					onClick={() => {
						if (canConfirm && sector && risk && tree) {
							onConfirm(sector, positions, risk, tree, template);
						}
					}}
				>
					Adicionar
				</Button>
			</DialogActions>
		</Dialog>
	);

	async function load() {
		setLoading(true);
		setFailed(false);
		const service = new RiskAssessmentService();
		const [structureResponse, treeResponse] = await Promise.all([
			service.getCompanyStructure(company.companyId),
			service.getLinks()
		]);
		setStructure(structureResponse?.data || null);
		setTree(treeResponse?.data || null);
		setFailed(!structureResponse || !treeResponse);
		setLoading(false);
	}
};

export default RiskAddRowModal;
